import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ContactIcon } from 'common-assets/assets/icons/contact-filled.svg';
import { ReactComponent as CloseIcon } from 'common-assets/assets/icons/closeX.svg';
import { Button } from '@material-ui/core';
import { ContactNumberText, HotlineInfo } from './ContactMenu';
import { useFocusTrap, useHotlineConfig } from 'common-react';
import { createHrefValueForPhoneNumber } from './tools/ContactMenu.utils';
import { Typography } from '../../Typography/Typography';
import { ReactComponent as PhoneIcon } from 'common-assets/assets/icons/icon-phone.svg';
import { Icon } from '../../Icon/Icon';

import useStyles from './ContactMenu.style';


const ContactMenuDesktop: React.FC = () => {

  const [t] = useTranslation('header');
  const c = useStyles({});
  const contactMenuId = 'contact-menu';
  const [contactMenuAnchorEl, setContactMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const wasMenuOpenRef = useRef<boolean>(false);
  const isContactMenuOpen = Boolean(contactMenuAnchorEl);
  const handleContactMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setContactMenuAnchorEl(event.currentTarget);
  };
  const handleContactMenuClose = () => {
    setContactMenuAnchorEl(null);
  };
  const focusTrapContainerRef = useFocusTrap(handleContactMenuClose);
  const hotlineConfig = useHotlineConfig();

  useEffect(() => {
    if (wasMenuOpenRef.current && !isContactMenuOpen) {
      menuButtonRef.current?.focus();
    }
    wasMenuOpenRef.current = isContactMenuOpen;
  }, [isContactMenuOpen]);

  return (
    <div className={c.root}>
      <button
        className={clsx(c.button, isContactMenuOpen ? c.buttonActive : '')}
        onClick={handleContactMenuOpen}
        ref={menuButtonRef}
        aria-haspopup="menu"
        aria-controls={contactMenuId}
        aria-expanded={isContactMenuOpen}
        aria-label={t('contact-menu.title')}
      >
        <Icon className={c.buttonIcon}>
          <ContactIcon />
        </Icon>
        <Typography component="span">{t("contact-menu.title")}</Typography>
      </button>
      <Menu
        ref={focusTrapContainerRef}
        classes={{
          list: c.menuList,
        }}
        PopoverClasses={{ paper: c.popover }}
        anchorEl={contactMenuAnchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        id={contactMenuId}
        disableRestoreFocus
        open={isContactMenuOpen}
        onClose={handleContactMenuClose}
        TransitionProps={{
          onExited: () => menuButtonRef.current?.focus()
        }}
      >
        <MenuItem
          disableGutters
          disabled
          className={c.credentialsBox}
        >
          <Typography variant="h4" fontWeight="bold">{t('contact-menu.title')}</Typography>
        </MenuItem>
        <MenuItem disabled className={c.menuItemPhoneHeader}>
          <Icon className={c.menuItemPhoneIcon}><PhoneIcon /></Icon>
          <Typography>{t("contact-menu.contact")}</Typography>
        </MenuItem>
        {hotlineConfig?.premiumPhoneNumber.link != null && hotlineConfig?.premiumPhoneNumber.formatted != null
            && <MenuItem
            className={clsx(c.menuItem, c.menuItemPhone)}
            data-testid='contact-number-menuitem'
            onClick={() => {
              window.location.href = createHrefValueForPhoneNumber(hotlineConfig?.premiumPhoneNumber.link ?? '');
            }}
            tabIndex={0}
          >
            <div className={clsx(c.menuItemBox, c.menuItemBoxPhone)}>
              <ContactNumberText number={hotlineConfig?.premiumPhoneNumber.formatted} />
            </div>
        </MenuItem>}
        {hotlineConfig?.standardPhoneNumber.link != null && hotlineConfig?.standardPhoneNumber.formatted != null
          && <MenuItem
            className={clsx(c.menuItem, c.menuItemPhone)}
            data-testid='contact-landline-number-menuitem'
            onClick={() => {
              window.location.href = createHrefValueForPhoneNumber(hotlineConfig?.standardPhoneNumber.link);
            }}
            tabIndex={0}
          >
            <div className={clsx(c.menuItemBox, c.menuItemBoxPhone)}>
              <ContactNumberText number={hotlineConfig?.standardPhoneNumber.formatted} />
            </div>
          </MenuItem>}
            <Button
              onClick={handleContactMenuClose}
              className={clsx(c.closeButton, 'visually-hidden')}
              aria-label={t('contact-menu.close-menu')}
            >
              <CloseIcon key="contact-close-icon" aria-hidden={true} focusable={false}/>
            </Button>
          <HotlineInfo key="hotline-info" hotlineConfig={hotlineConfig}/>
      </Menu>
    </div>
  );
}

export default ContactMenuDesktop;
